import {
  canBuyProduct,
  getAvailableVariantValues,
  getItemType,
  getProductId,
  getProductVariants,
  getVariantAttributes,
  isProductActive,
} from "./productAdapter";

const VALID_CATALOG_MODES = new Set(["auto", "products", "services", "mixed"]);
const HYBRID_TYPE_NAMES = new Set(["hybrid", "hibrido", "híbrido"]);

function normalizedText(value) {
  return String(value || "").trim().toLowerCase();
}

function booleanValue(value) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || normalizedText(value) === "true") return true;
  if (value === 0 || value === "0" || normalizedText(value) === "false") return false;
  return undefined;
}

function firstBoolean(values, fallback = false) {
  for (const value of values) {
    const normalized = booleanValue(value);
    if (normalized !== undefined) return normalized;
  }
  return fallback;
}

function declaredItemType(item) {
  return normalizedText(
    item?.catalogItemType ||
      item?.itemType ||
      item?.type ||
      item?.tipo ||
      item?.erpItemKind ||
      item?.itemKind,
  );
}

export function getCatalogCapabilities(item = {}) {
  const baseType = getItemType(item);
  const supplied = {
    ...(item?.capabilities && typeof item.capabilities === "object"
      ? item.capabilities
      : {}),
    ...(item?.operational && typeof item.operational === "object"
      ? item.operational
      : {}),
  };

  return {
    sellable: firstBoolean(
      [supplied.sellable, item?.sellable],
      baseType === "product" || baseType === "service",
    ),
    stockTracked: firstBoolean(
      [supplied.stockTracked, item?.stockTracked, item?.tracksStock],
      baseType === "product",
    ),
    reservable: firstBoolean(
      [
        supplied.reservable,
        item?.reservable,
        item?.requiresBooking,
        item?.requiereReserva,
      ],
      false,
    ),
    shippable: firstBoolean(
      [
        supplied.shippable,
        item?.shippable,
        item?.requiresShipping,
        item?.requiereEnvio,
      ],
      baseType === "product",
    ),
    publicVisible: firstBoolean(
      [supplied.publicVisible, item?.publicVisible],
      true,
    ),
  };
}

export function getCatalogItemKind(item = {}) {
  const declaredType = declaredItemType(item);
  if (HYBRID_TYPE_NAMES.has(declaredType)) return "hybrid";

  const baseType = getItemType(item);
  const capabilities = getCatalogCapabilities(item);
  const combinesProductAndService =
    (baseType === "product" && capabilities.reservable) ||
    (baseType === "service" && (capabilities.stockTracked || capabilities.shippable));

  if (combinesProductAndService) return "hybrid";
  return baseType === "service" ? "service" : "product";
}

export function catalogItemLabel(item) {
  const kind = getCatalogItemKind(item);
  if (kind === "hybrid") return "Producto + servicio";
  if (kind === "service") return "Servicio";
  return "Producto";
}

export function catalogItemMatchesType(item, type = "") {
  const normalizedType = normalizedText(type);
  if (!normalizedType) return true;

  const kind = getCatalogItemKind(item);
  if (normalizedType === "hybrid") return kind === "hybrid";
  if (normalizedType === "service") return kind === "service" || kind === "hybrid";
  if (normalizedType === "product") return kind === "product" || kind === "hybrid";
  return true;
}

export function canAddCatalogItemToCart(item, variant = null) {
  const capabilities = getCatalogCapabilities(item);
  const kind = getCatalogItemKind(item);

  // HOTFIX 2026-10: `stockTracked` sólo gobierna inventario. Un producto
  // vendible sin control de stock conserva variantes, carrito, checkout y pago.
  // Los servicios puros siguen usando su flujo de consulta/reserva.
  if (!capabilities.sellable || kind === "service") return false;
  if (String(item?.precioMoneda || item?.priceCurrency || item?.currency || item?.pricing?.currency || "ARS").toUpperCase() === "USD") return false;
  return canBuyProduct(item, variant);
}

export function getAvailableCatalogVariantValues(item, axisKey, selection = {}) {
  const capabilities = getCatalogCapabilities(item);
  if (capabilities.stockTracked) {
    return getAvailableVariantValues(item, axisKey, selection);
  }

  const axes = getVariantAttributes(item);
  const variants = getProductVariants(item);
  const axisIndex = axes.findIndex((axis) => axis.key === axisKey);
  const previousAxes = axes.slice(0, Math.max(0, axisIndex));
  const matchingVariants = variants.filter((variant) =>
    previousAxes.every((axis) => {
      const selected = selection?.[axis.key];
      return !selected || String(variant?.attributes?.[axis.key] || "") === String(selected);
    }),
  );

  return new Set(
    matchingVariants
      .map((variant) => String(variant?.attributes?.[axisKey] || ""))
      .filter(Boolean),
  );
}

export function normalizeCatalogMode(value) {
  const mode = normalizedText(value || "auto");
  return VALID_CATALOG_MODES.has(mode) ? mode : "auto";
}

export function resolveCatalogMode(configuredMode, items = []) {
  const mode = normalizeCatalogMode(configuredMode);
  if (mode !== "auto") return mode;

  const activeItems = Array.isArray(items) ? items.filter(isProductActive) : [];
  const hasProducts = activeItems.some((item) => catalogItemMatchesType(item, "product"));
  const hasServices = activeItems.some((item) => catalogItemMatchesType(item, "service"));

  if (hasServices && !hasProducts) return "services";
  if (hasProducts && !hasServices) return "products";
  return "mixed";
}

export function catalogItemType(mode) {
  const normalized = normalizeCatalogMode(mode);
  if (normalized === "products") return "product";
  if (normalized === "services") return "service";
  return "";
}

export function filterCatalogItems(items = [], mode = "mixed") {
  const itemType = catalogItemType(mode);
  if (!itemType) return items;
  return items.filter((item) => catalogItemMatchesType(item, itemType));
}

function canonicalCatalogTarget(configuredProductsUrl = "/productos") {
  const target = String(configuredProductsUrl || "/productos").trim() || "/productos";
  const hashRoute = target.startsWith("#/");
  const internalTarget = hashRoute ? target.slice(1) : target;
  if (!internalTarget.startsWith("/")) return target;

  const [pathAndSearch, fragment = ""] = internalTarget.split("#", 2);
  const queryIndex = pathAndSearch.indexOf("?");
  const pathname = queryIndex >= 0 ? pathAndSearch.slice(0, queryIndex) : pathAndSearch;
  const search = queryIndex >= 0 ? pathAndSearch.slice(queryIndex + 1) : "";
  const canonicalPath = ["/servicios", "/catalogo"].includes(pathname)
    ? "/productos"
    : pathname || "/productos";

  return {
    hashRoute,
    pathname: canonicalPath,
    search,
    fragment,
  };
}

export function catalogListPath(mode, configuredProductsUrl = "/productos") {
  const target = canonicalCatalogTarget(configuredProductsUrl);
  if (typeof target === "string") return target;

  const params = new URLSearchParams(target.search || "");
  const itemType = catalogItemType(mode);
  if (itemType) params.set("tipo", itemType);
  else params.delete("tipo");

  const query = params.toString();
  return `${target.hashRoute ? "#" : ""}${target.pathname}${query ? `?${query}` : ""}${
    target.fragment ? `#${target.fragment}` : ""
  }`;
}

export function catalogDetailPath(item) {
  const id = String(getProductId(item) || "").trim();
  return id ? `/producto/${encodeURIComponent(id)}` : "/productos";
}

export function catalogBookingPath(item) {
  const id = String(getProductId(item) || "").trim();
  const { reservable } = getCatalogCapabilities(item);
  return id && reservable
    ? `/reservar/${encodeURIComponent(id)}`
    : catalogDetailPath(item);
}

export function catalogLabel(mode, configuredLabel) {
  const normalized = normalizeCatalogMode(mode);
  const hasConfiguredLabel = configuredLabel !== undefined && configuredLabel !== null;
  const label = String(configuredLabel ?? "").trim();

  // Un label vacío enviado por SiteSettings es una decisión editorial, no la
  // ausencia de configuración. Los defaults dinámicos sólo se aplican cuando
  // el campo realmente no existe.
  if (normalized === "services") {
    return !hasConfiguredLabel || /^productos(?: y servicios)?$/i.test(label)
      ? "Servicios"
      : label;
  }
  if (normalized === "mixed") {
    return !hasConfiguredLabel || /^productos$/i.test(label) ? "Catálogo" : label;
  }
  return hasConfiguredLabel ? label : "Productos";
}

export function resolveCatalogModeFromSummary(configuredMode, summary = {}) {
  const mode = normalizeCatalogMode(configuredMode);
  if (mode !== "auto") return mode;

  const productCount = Number(summary?.productCount || 0);
  const serviceCount = Number(summary?.serviceCount || 0);
  if (serviceCount > 0 && productCount <= 0) return "services";
  if (productCount > 0 && serviceCount <= 0) return "products";
  return "mixed";
}
